import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { api } from '../../services/api';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const heroImage = isDesktop
    ? `${process.env.PUBLIC_URL}/login-bg.webp`
    : `${process.env.PUBLIC_URL}/login-bg-mobile.webp`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
      showToast('Check your email', 'success');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY>
        <div className="fh-landing">
          <div
            className="fh-landing__photo"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="fh-landing__panel">
            <div className="fh-auth__inner">
              <button
                onClick={() => history.push('/login')}
                className="fh-auth__back"
                aria-label="Back to login"
                style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 44 }}
              >
                <Icon name="chevron_left" size={20} color="#c5512d" />
                Back
              </button>

              <div className="fh-auth__header">
                <img src="/icon-nobg.png" alt="FamHearth" className="fh-auth__icon" />
                <h1 className="fh-auth__title">Forgot password?</h1>
                <p className="fh-auth__subtitle">
                  {submitted
                    ? 'Check your inbox for a reset link.'
                    : "Enter your email and we'll send you a link to reset it."}
                </p>
              </div>

              {submitted ? (
                <div className="fh-auth__form">
                  <div style={{
                    background: 'rgba(42,157,143,0.08)',
                    border: '1px solid rgba(42,157,143,0.25)',
                    borderRadius: 12, padding: '14px 16px',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <Icon name="mark_email_read" size={20} color="#2A9D8F" />
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#2A9D8F', fontSize: 13, fontWeight: 600, margin: 0 }}>
                        If an account exists for <strong>{email}</strong>, a password reset link is on its way.
                      </p>
                      <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, margin: '6px 0 0' }}>
                        The link expires in 1 hour. Check your spam folder if you don't see it.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => history.push('/login')}
                    className="fh-auth__submit"
                    style={{ minHeight: 44, marginTop: 8 }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} aria-label="Forgot password form" className="fh-auth__form">
                  <div className="fh-auth__field">
                    <label className="fh-auth__label">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@example.com"
                      className="fh-auth__input"
                      autoFocus
                    />
                  </div>
                  {error && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'rgba(239,83,80,0.08)',
                      border: '1px solid rgba(239,83,80,0.2)',
                      borderRadius: 12, padding: '10px 14px',
                    }}>
                      <Icon name="warning" size={18} color="#ef5350" />
                      <p style={{ color: '#ef5350', fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>
                    </div>
                  )}
                  <button type="submit" disabled={isLoading} className="fh-auth__submit" style={{ minHeight: 44 }}>
                    {isLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          animation: 'fh-spin 0.6s linear infinite',
                          display: 'inline-block',
                        }} />
                        Sending...
                      </span>
                    ) : 'Send reset link'}
                  </button>
                </form>
              )}

              <p className="fh-auth__footer">
                Remembered it?{' '}
                <button onClick={() => history.push('/login')} className="fh-auth__link" style={{ minHeight: 44 }}>
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ForgotPasswordPage;
